// Validação e formatação de CPF e CNPJ (algoritmo dos dígitos verificadores).

export function soDigitos(v: string): string {
  return (v ?? '').replace(/\D/g, '');
}

// ───────── CPF ─────────
export function validarCpf(v: string): boolean {
  const d = soDigitos(v);
  if (d.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(d)) return false; // todos iguais

  const calc = (qtd: number): number => {
    let soma = 0;
    for (let i = 0; i < qtd; i++) soma += parseInt(d[i], 10) * (qtd + 1 - i);
    const resto = (soma * 10) % 11;
    return resto === 10 ? 0 : resto;
  };
  return calc(9) === parseInt(d[9], 10) && calc(10) === parseInt(d[10], 10);
}

export function formatarCpf(v: string): string {
  const d = soDigitos(v).slice(0, 11);
  return d
    .replace(/^(\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d{1,2})$/, '.$1-$2');
}

// ───────── CNPJ (alfanumérico — IN RFB 2.229/2024) ─────────
//
// A partir da IN RFB 2.229/2024 o CNPJ pode conter LETRAS nas 12 primeiras posições
// (raiz + ordem); os 2 últimos dígitos verificadores continuam SEMPRE numéricos.
// Layout: AA.AAA.AAA/AAAA-99, onde A ∈ [0-9A-Z].
//
// ⚠️ NÃO use `soDigitos` para normalizar CNPJ — ela apaga as letras e corrompe o
// documento. Use `soAlfanumCnpj`.

/** Normaliza CNPJ preservando letras: maiúsculas, só [0-9A-Z], máx. 14. */
export function soAlfanumCnpj(v: string): string {
  return (v ?? '').toUpperCase().replace(/[^0-9A-Z]/g, '').slice(0, 14);
}

// Valor do caractere no cálculo do DV: código ASCII − 48.
// '0'..'9' → 0..9 | 'A' → 17 | 'B' → 18 | ... | 'Z' → 42.
// Para CNPJ puramente numérico o resultado é idêntico ao algoritmo antigo,
// então a mudança é retrocompatível.
function valorCaractere(c: string): number {
  return c.charCodeAt(0) - 48;
}

export function validarCnpj(v: string): boolean {
  const d = soAlfanumCnpj(v);
  if (d.length !== 14) return false;
  // DV precisa ser numérico mesmo no CNPJ alfanumérico.
  if (!/^\d{2}$/.test(d.slice(12))) return false;
  // Rejeita repetição total (00000000000000, AAAAAAAAAAAAAA…).
  if (/^(.)\1{13}$/.test(d)) return false;

  const calc = (base: string): number => {
    let soma = 0;
    let peso = base.length - 7;
    for (let i = 0; i < base.length; i++) {
      soma += valorCaractere(base[i]) * peso;
      peso = peso === 2 ? 9 : peso - 1;
    }
    const resto = soma % 11;
    return resto < 2 ? 0 : 11 - resto;
  };
  const dv1 = calc(d.slice(0, 12));
  const dv2 = calc(d.slice(0, 12) + String(dv1));
  return dv1 === parseInt(d[12], 10) && dv2 === parseInt(d[13], 10);
}

export function formatarCnpj(v: string): string {
  const d = soAlfanumCnpj(v);
  // Fatiar por posição (em vez de regex sobre \d) é o que permite letras.
  const p = [d.slice(0, 2), d.slice(2, 5), d.slice(5, 8), d.slice(8, 12), d.slice(12, 14)];
  let out = p[0];
  if (p[1]) out += '.' + p[1];
  if (p[2]) out += '.' + p[2];
  if (p[3]) out += '/' + p[3];
  if (p[4]) out += '-' + p[4];
  return out;
}

// ───────── Genérico ─────────
export type TipoDocumento = 'cpf' | 'cnpj';

export function formatarDoc(tipo: TipoDocumento, v: string): string {
  return tipo === 'cpf' ? formatarCpf(v) : formatarCnpj(v);
}

export function validarDoc(tipo: TipoDocumento, v: string): boolean {
  return tipo === 'cpf' ? validarCpf(v) : validarCnpj(v);
}

// Comprimento esperado (para saber se o usuário terminou de digitar).
// CNPJ conta caracteres alfanuméricos (IN RFB 2.229/2024); CPF só dígitos.
export function docCompleto(tipo: TipoDocumento, v: string): boolean {
  return tipo === 'cpf'
    ? soDigitos(v).length === 11
    : soAlfanumCnpj(v).length === 14;
}

/** Normaliza para envio à API conforme o tipo (CNPJ preserva letras). */
export function normalizarDoc(tipo: TipoDocumento, v: string): string {
  return tipo === 'cpf' ? soDigitos(v) : soAlfanumCnpj(v);
}

/**
 * Máscara para EXIBIÇÃO (listagens, detalhes, cards). Detecta o tipo pelo
 * comprimento — útil onde o campo é genérico (ora CPF, ora CNPJ).
 *
 * Regras deliberadas:
 * - vazio/null/undefined → `fallback` (default '—'), preservando o padrão das telas;
 * - 11 dígitos → CPF; 14 dígitos → CNPJ;
 * - **qualquer outro comprimento → devolve o valor ORIGINAL**. O banco de dev tem
 *   documento sujo (já formatado, truncado, com lixo); mascarar à força produziria
 *   algo pior que o dado cru. Degradar é intencional — nunca quebrar a tela.
 *
 * Para INPUT use `DocInput` (máscara enquanto digita + validação de DV).
 */
export function formatarDocExibicao(
  v: string | null | undefined,
  fallback = '—',
): string {
  if (v === null || v === undefined) return fallback;
  const bruto = String(v).trim();
  if (bruto === '') return fallback;

  // CNPJ pode ter letras (IN RFB 2.229/2024), então a detecção olha primeiro o
  // alfanumérico. CPF continua exclusivamente numérico.
  const alfa = soAlfanumCnpj(bruto);
  if (alfa.length === 14) return formatarCnpj(alfa);

  const d = soDigitos(bruto);
  if (d.length === 11) return formatarCpf(d);
  return bruto;
}
